import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import activoClassificationService from '../../../services/activoClassificationService';
import { getAreas } from '../../../services/areaService';

const STATUS_OPTIONS = [
  { value: 'pending_start', label: 'Pendiente inicio de actividades' },
  { value: 'active', label: 'Activo' },
  { value: 'maintenance', label: 'En mantenimiento' },
  { value: 'inactive', label: 'Inactivo' },
  { value: 'retired', label: 'Retirado' },
];

const emptyForm = {
  code: '',
  name: '',
  description: '',
  category_id: '',
  subcategory_id: '',
  activo_family_id: '',
  area_id: '',
  brand: '',
  model: '',
  serial_number: '',
  status: 'pending_start',
  notes: '',
  is_active: true,
};

function findFamilyPath(family) {
  if (!family) return '—';
  const sub = family.subcategory;
  const cat = sub?.category;
  const parts = [cat?.name, sub?.name, family.name].filter(Boolean);
  return parts.join(' › ') || family.name;
}

const ActivoForm = ({ initialData = null, onSubmit, loading, submitLabel = 'Guardar' }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [hierarchy, setHierarchy] = useState([]);
  const [areas, setAreas] = useState([]);
  const [formLoading, setFormLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      activoClassificationService.getHierarchy({ active_only: true }),
      getAreas({ active_only: true }),
    ])
      .then(([hierarchyRes, areasRes]) => {
        if (cancelled) return;
        if (hierarchyRes.success && Array.isArray(hierarchyRes.data)) {
          setHierarchy(hierarchyRes.data);
        }
        if (areasRes.success && Array.isArray(areasRes.data)) {
          setAreas(areasRes.data);
        }
      })
      .finally(() => {
        if (!cancelled) setFormLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!initialData) {
      setFormData(emptyForm);
      return;
    }
    const family = initialData.family;
    const sub = family?.subcategory;
    const cat = sub?.category;
    setFormData({
      code: initialData.code || '',
      name: initialData.name || '',
      description: initialData.description || '',
      category_id: cat?.id ? String(cat.id) : '',
      subcategory_id: sub?.id ? String(sub.id) : '',
      activo_family_id: initialData.activo_family_id ? String(initialData.activo_family_id) : '',
      area_id: initialData.area_id ? String(initialData.area_id) : '',
      brand: initialData.brand || '',
      model: initialData.model || '',
      serial_number: initialData.serial_number || '',
      status: initialData.status || 'active',
      notes: initialData.notes || '',
      is_active: initialData.is_active !== undefined ? initialData.is_active : true,
    });
  }, [initialData]);

  const subcategories = useMemo(() => {
    if (!formData.category_id) return [];
    const cat = hierarchy.find((c) => String(c.id) === String(formData.category_id));
    return cat?.subcategories || [];
  }, [hierarchy, formData.category_id]);

  const families = useMemo(() => {
    if (!formData.subcategory_id) return [];
    const sub = subcategories.find((s) => String(s.id) === String(formData.subcategory_id));
    return sub?.families || [];
  }, [subcategories, formData.subcategory_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'category_id') {
        next.subcategory_id = '';
        next.activo_family_id = '';
      }
      if (name === 'subcategory_id') {
        next.activo_family_id = '';
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      code: formData.code.trim(),
      name: formData.name.trim(),
      description: formData.description.trim() || null,
      activo_family_id: formData.activo_family_id ? parseInt(formData.activo_family_id, 10) : null,
      area_id: formData.area_id ? parseInt(formData.area_id, 10) : null,
      brand: formData.brand.trim() || null,
      model: formData.model.trim() || null,
      serial_number: formData.serial_number.trim() || null,
      status: formData.status,
      notes: formData.notes.trim() || null,
      is_active: formData.is_active,
    });
  };

  if (formLoading) {
    return <div className="text-gray-600 py-8">Cargando formulario…</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Código *</label>
          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            required
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
          <select
            name="category_id"
            value={formData.category_id}
            onChange={handleChange}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Seleccione…</option>
            {hierarchy.map((c) => (
              <option key={c.id} value={c.id}>{c.code} — {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Subcategoría</label>
          <select
            name="subcategory_id"
            value={formData.subcategory_id}
            onChange={handleChange}
            disabled={!formData.category_id}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
          >
            <option value="">Seleccione…</option>
            {subcategories.map((s) => (
              <option key={s.id} value={s.id}>{s.code} — {s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Familia</label>
          <select
            name="activo_family_id"
            value={formData.activo_family_id}
            onChange={handleChange}
            disabled={!formData.subcategory_id}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 disabled:bg-gray-100"
          >
            <option value="">Seleccione…</option>
            {families.map((f) => (
              <option key={f.id} value={f.id}>{f.code} — {f.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Área de empresa *</label>
          <select
            name="area_id"
            value={formData.area_id}
            onChange={handleChange}
            required
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="">Seleccione el área donde está el activo…</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
          {areas.length === 0 && (
            <p className="text-xs text-amber-600 mt-1">
              No hay áreas activas. Regístrelas en{' '}
              <Link to="/activos/configuracion/areas" className="underline hover:text-amber-700">
                Activos → Configuración → Áreas
              </Link>
              .
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Marca</label>
          <input type="text" name="brand" value={formData.brand} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Modelo</label>
          <input type="text" name="model" value={formData.model} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">N° serie</label>
          <input type="text" name="serial_number" value={formData.serial_number} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Estado operativo</label>
          <select name="status" value={formData.status} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">Notas</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} rows={2} className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" id="is_active" name="is_active" checked={formData.is_active} onChange={handleChange} className="rounded border-gray-300 text-teal-600 focus:ring-teal-500" />
          <label htmlFor="is_active" className="text-sm text-gray-700">Registro activo en el sistema</label>
        </div>
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium disabled:opacity-50"
        >
          {loading ? 'Guardando…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export { findFamilyPath, STATUS_OPTIONS };
export default ActivoForm;
